/************************************************************************************

* [UTILITY FUNCTIONS]

*************************************************************************************/
class LTA_SectionId {
	static #separator = '__'

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
	static parseId(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[0]
	}

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
	static parseSectionName(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[1]
	}

	// for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
	static getIdForSection(sectionId, sectionName) {
		return `${sectionId}${LTA_SectionId.#separator}${sectionName}`
	}
}

class LTA_HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent))

		const newNodeWrapper = document.createElement('div')
		LTA_HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
		const newNode = newNodeWrapper.firstChild

		// dedupe IDs
		const uniqueKey = Date.now()
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`)
			element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
		})

		oldNode.parentNode.insertBefore(newNode, oldNode)
		oldNode.style.display = 'none'

		postProcessCallbacks?.forEach((callback) => callback(newNode))

		setTimeout(() => oldNode.remove(), 500)
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}
}

function l_getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	).filter((element) => !element.closest('[aria-hidden="true"]'))
}

function l_disableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)
	focusableElements.forEach((element) => {
		element.setAttribute('tabindex', '-1')
		element.setAttribute('aria-disabled', 'true')
	})
}

function l_enableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)

	focusableElements.forEach((element) => {
		element.removeAttribute('tabindex')
		element.removeAttribute('aria-disabled')
	})
}

function l_debounce(fn, wait) {
	let t

	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

const l_trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus, enableFocusRange = true) {
	const elements = l_getFocusableElements(container)
	const first = elements[0]
	const last = elements[elements.length - 1]
	const elementToFocus = defaultElementToFocus || first

	l_removeTrapFocus()

	l_trapFocusHandlers.focusin = (event) => {
		if (!container.contains(event.target)) return

		document.addEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	l_trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	l_trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key

		// On the last focusable element and tab forward, focus the first element.
		if ((event.target === last || last.contains(event.target)) && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || first.contains(event.target)) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', l_trapFocusHandlers.focusout)
	document.addEventListener('focusin', l_trapFocusHandlers.focusin)

	if (elementToFocus) {
		elementToFocus.focus()

		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value &&
			enableFocusRange
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length)
		}
	}
}

function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', l_trapFocusHandlers.focusin)
	document.removeEventListener('focusout', l_trapFocusHandlers.focusout)
	document.removeEventListener('keydown', l_trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}

function l_pauseAllMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.pauseVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
		youtubeVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
		vimeoVideo.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	container.querySelectorAll('video').forEach((video) => video.pause())
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.pause()
	})
}

function l_playMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.playVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
		youtubeVideo.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
		vimeoVideo.contentWindow.postMessage('{"method":"play"}', '*')
	})
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.play()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.play()
	})
}

function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.LTATheme.money.format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', ',')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function l_lockBodyScroll(elementName) {
	document.body.classList.add('js-block-page')

	if (elementName) {
		document.body.classList.add(`js-block-page-${elementName}`)
	}
}

function l_unLockBodyScroll(elementName) {
	if (elementName) {
		document.body.classList.remove(`js-block-page-${elementName}`)

		const isExistAnotherBlockedClass = Array.from(document.body.classList).some((cls) =>
			cls.startsWith('js-block-page-')
		)

		!isExistAnotherBlockedClass && document.body.classList.remove('js-block-page')
	} else {
		document.body.classList.remove('js-block-page')
	}
}

function l_setupGlobalVariables() {
	// Set scrollbar width
	const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
	document.documentElement.style.setProperty('--l-body-scrollbar-width', `${scrollBarWidth}px`)
}

function l_setupVariablesBeforeOpenModal() {
	// Elements
	const announcementBarSection = document.getElementById('announcementBar')
	const headerSection = document.getElementById('header')

	// Set CSS variables
	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerSection?.offsetHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${announcementBarSection?.offsetHeight || 0}px`
	)

	return {
		headerHeight: headerSection?.offsetHeight || 0,
		announcementBarHeight: announcementBarSection?.offsetHeight || 0,
		headerTotalHeight: (headerSection?.offsetHeight || 0) + (announcementBarSection?.offsetHeight || 0),
		viewportHeight: visualViewport?.height ?? window.innerHeight,
	}
}

function l_smoothScrollOnAnchorTags() {
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		anchor.addEventListener('click', (e) => {
			e.preventDefault()

			const target = document.querySelector(anchor.getAttribute('href'))
			target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
		})
	})
}

function l_addClassesToRteTables() {
	document.querySelectorAll('.l-rte table, .shopify-policy__body .rte table').forEach((table) => {
		const wrapper = document.createElement('div')
		wrapper.classList.add('l-rte-table', 'l-overflow-x-auto', 'l-scrollbar--hidden')
		table.parentNode.insertBefore(wrapper, table)
		wrapper.appendChild(table)
	})
}

/***********************************
 * The following functions are rarely used or almost unused in the project
 ************************************/

const l_getElement = (targetSelector, container) => {
	if (targetSelector instanceof Element) return targetSelector
	if (typeof targetSelector === 'string') {
		return targetSelector.startsWith('#')
			? container.getElementById(targetSelector.slice(1))
			: container.querySelector(targetSelector)
	}

	return null
}

function l_watchDOMChanges({ target, container = document, callback, once = false }) {
	const watchedEl = l_getElement(target, container)
	if (!watchedEl) return null

	const observer = new MutationObserver((mutationsList, obs) => {
		for (const mutation of mutationsList) {
			if (mutation.type === 'childList' && mutation.addedNodes.length) {
				callback(mutation.addedNodes, watchedEl)
				if (once) {
					obs.disconnect()
					break
				}
			}
		}
	})

	observer.observe(watchedEl, {
		attributes: true,
		childList: true,
		subtree: true,
	})

	return observer
}

function l_isElementInViewport(el) {
	const rect = el.getBoundingClientRect()
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	)
}

function l_normalizeString(str) {
	return str
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase()
}

function l_handleize(str) {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.replace(/--+/g, '-')
}

function l_stepChain(steps, onDone) {
	//   stepChain(
	//   [
	//     { action: () => console.log('A'), delay: 0 },
	//     { action: () => console.log('B'), delay: 300 },
	//   ],
	//   () => console.log('✅ Chain done')
	// )

	const timeouts = []
	let totalDelay = 0
	let isCancelled = false

	steps.forEach(({ action, delay }, index) => {
		totalDelay += delay
		const timeoutId = setTimeout(() => {
			if (isCancelled) return
			action()
			if (index === steps.length - 1 && onDone) onDone()
		}, totalDelay)
		timeouts.push(timeoutId)
	})

	return {
		cancel: () => {
			isCancelled = true
			timeouts.forEach(clearTimeout)
		},
	}
}

function l_throttle(fn, delay) {
	let lastCall = 0
	let timeout = null

	return function (...args) {
		const now = Date.now()
		const remaining = delay - (now - lastCall)

		if (remaining <= 0) {
			if (timeout) {
				clearTimeout(timeout)
				timeout = null
			}
			lastCall = now
			fn(...args)
		} else if (!timeout) {
			timeout = setTimeout(() => {
				lastCall = Date.now()
				timeout = null
				fn(...args)
			}, remaining)
		}
	}
}

function l_addEventDelegate({
	context = document.documentElement,
	event = 'click',
	selector,
	handler,
	capture = false,
}) {
	if (!context) return

	const listener = function (e) {
		// loop parent nodes from the target to the delegation node
		for (let target = e.target; target && target !== this; target = target.parentNode) {
			if (target.matches(selector)) {
				handler.call(target, e, target)
				break
			}
		}
	}

	context.addEventListener(event, listener, capture)

	return () => {
		context.removeEventListener(event, listener, capture)
	}
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

function l_preloadImage(container) {
	const imageEls = container.querySelectorAll('img')

	imageEls.forEach((imageEl) => {
		if (imageEl.complete) return

		const image = new Image()
		image.src = imageEl.src
	})
}

function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

function l_isReturningVisitorAfterSession() {
	const FIRST_VISIT_KEY = 'hasVisited'
	const SESSION_KEY = 'sessionStarted'

	let firstVisitId = localStorage.getItem(FIRST_VISIT_KEY)
	let currentSessionId = sessionStorage.getItem(SESSION_KEY)

	// If no session, create a new session ID
	if (!currentSessionId) {
		currentSessionId = Date.now().toString()
		sessionStorage.setItem(SESSION_KEY, currentSessionId)
	}

	// If first visit, store the session ID in localStorage
	if (!firstVisitId) {
		localStorage.setItem(FIRST_VISIT_KEY, currentSessionId)
		firstVisitId = currentSessionId
	}

	const isReturning = firstVisitId !== currentSessionId

	document.dispatchEvent(
		new CustomEvent('l:detectReturningCustomers', {
			bubbles: true,
			detail: { isReturningCustomer: isReturning },
		})
	)
}

/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
	constructor() {}

	getBaseConfig() {
		return {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
	}

	createConfigFromForm(formEl) {
		const config = this.getBaseConfig()
		const formData = new FormData(formEl)

		config.body = this.appendCartDrawerData(formData)

		return config
	}

	createConfigFromItems(items = []) {
		const config = this.getBaseConfig()
		const formData = new FormData()

		items.forEach((item, index) => {
			const prefix = `items[${index}]`

			formData.append(`${prefix}[id]`, item.id)
			formData.append(`${prefix}[quantity]`, item.quantity)

			// Add all other properties from the item object except id, quantity, and selling_plan
			Object.entries(item).forEach(([key, value]) => {
				if (['id', 'quantity'].includes(key)) return
				// If the value is an object, serialize it as JSON
				if (typeof value === 'object' && value !== null) {
					formData.append(`${prefix}[${key}]`, JSON.stringify(value))
				} else if (value !== undefined) {
					formData.append(`${prefix}[${key}]`, value)
				}
			})
		})

		config.body = this.appendCartDrawerData(formData)
		return config
	}

	appendCartDrawerData(formData) {
		const cartDrawerEl = document.getElementById('cart-drawer') || document.getElementById('cart-notification')

		if (cartDrawerEl) {
			formData.append(
				'sections',
				cartDrawerEl.getSectionsToRender().map((section) => section.id)
			)
			formData.append('sections_url', window.location.pathname)
			cartDrawerEl.setActiveElement(document.activeElement)
		}

		return formData
	}

	async addToCart(config, id = 'default') {
		// Note: Don't use finally / catch here

		return fetch(`${window.LTATheme.routes.cart_add_url}.js`, config)
			.then((res) => res.json())
			.then((data) => {
				if (data?.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

					return data
				}

				document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}

	async updateCartLineItems(updates, id = 'default') {
		return fetch(`${window.LTATheme.routes.cart_update_url}.js`, {
			...l_fetchConfig(),
			body: JSON.stringify({ updates }),
		})
			.then((res) => res.json())
			.then((data) => {
				document.dispatchEvent(new CustomEvent('l:product:updated', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}

	async changeCartLineItem(formData, id = 'default') {
		return fetch(`${window.LTATheme.routes.cart_change_url}.js`, { ...l_fetchConfig(), body: JSON.stringify(formData) })
			.then((res) => res.json())
			.then((data) => {
				document.dispatchEvent(new CustomEvent('l:product:changed', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}
}

const ltaCartService = new LTACartServices()

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: COLLAPSIBLE TAB
 ******************************************/

class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerEl = this.querySelector('[data-trigger]')
		this.contentEl = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = true
		this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

		this.transitionendEvent = this.transitionendEventName()

		// Setup default behavior / data
		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.contentEl.removeAttribute('hidden')
			this.contentEl.classList.remove('l-hidden')
			this.classList.add(this.expandedClass)
			this.setExpandedAria(false)
		} else {
			this.contentEl.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria(null, false)
		}

		// Events
		this.triggerEl.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()

		this.setExpandedAria()
	}

	close() {
		this.selected = false
		this.collapse()

		this.setCollapsedAria()
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.contentEl.style.transition = '0'
						this.contentEl.style.height = 'auto'

						requestAnimationFrame(() => {
							this.contentEl.style.height = null
							this.contentEl.style.transition = null
						})
					})
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true

		this.contentEl.removeAttribute('hidden')
		this.contentEl.classList.remove('l-hidden')

		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)

		this.contentEl.style.height = this.contentEl.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.classList.add(this.collapsedClass)
				}

				this.contentEl.setAttribute('hidden', 'hidden')
				this.contentEl.classList.add('l-hidden')
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.contentEl.style.transition = '0'
			this.contentEl.style.height = this.contentEl.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.contentEl.style.transition = null
				this.contentEl.style.height = this.collapsedHeight
			})
		})

		if (this.isScrollIntoView) {
			l_debounce(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)()
		}
	}

	destroy() {
		this.triggerEl && this.triggerEl.removeEventListener('click', (e) => this.toggle(e))
		this.contentEl && this.contentEl.removeAttribute('aria-hidden')
		this.contentEl && (this.contentEl.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	setExpandedAria(autoFocus = true) {
		this.setAttribute('open', true)

		this.triggerEl.setAttribute('aria-expanded', 'true')
		this.contentEl.removeAttribute('aria-hidden')
		this.contentEl.removeAttribute('tabindex')

		if (autoFocus) {
			l_enableFocusableElements(this.contentEl)
			l_trapFocus(this)
		}
	}

	setCollapsedAria(elementToFocus = this.triggerEl, enableTrapFocus = true) {
		this.removeAttribute('open')

		this.triggerEl.setAttribute('aria-expanded', 'false')
		this.contentEl.setAttribute('aria-hidden', 'true')
		this.contentEl.setAttribute('tabindex', '-1')

		l_disableFocusableElements(this.contentEl)
		enableTrapFocus && l_removeTrapFocus(elementToFocus)
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		const manualInit = this.dataset.manualInit === 'true'

		if (!manualInit) this.init()
	}

	init() {
		// Check if Swiper is already loaded
		if (window.Swiper) {
			this.setupCarousel()

			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	setupCarousel() {
		this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

		if (!this.carouselConfig) return

		// Initial carousel
		this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)

		// Design mode scroll
		Shopify.designMode && this.designModeScroll()

		// Dispatch event
		const dispatchEvent = this.dataset.dispatchEvent === 'true'

		dispatchEvent &&
			this.dispatchEvent(
				new CustomEvent('l:swiper:carousel:loaded', {
					bubbles: true,
					detail: {
						id: this.id || this.dataset.id || 'default',
					},
				})
			)
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl == swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0) {
		this.swiperCarousel.slideTo(index)
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/

class LTAMediaVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.videoContainerEl = this.querySelector('[data-video-container]') || this

		// Click
		this.addEventListener('click', (event) => {
			const hasLoaded = this.classList.contains('is-loaded')
			const isPlaying = this.classList.contains('is-playing')

			hasLoaded ? (isPlaying ? this.pauseVideo() : this.playVideo()) : this.initPlayer(event)
		})
	}

	initPlayer(event) {
		event.preventDefault()

		const templateEl = this.querySelector('template')

		if (!templateEl) return

		this.pauseOtherVideos()

		// Add template content to the element
		const templateContentEl = templateEl.content.cloneNode(true)

		this.videoContainerEl.appendChild(templateContentEl)
		this.classList.add('is-loaded', 'is-playing')
	}

	playVideo() {
		this.pauseOtherVideos()
		l_playMedia(this)

		this.classList.add('is-playing')
		this.classList.remove('is-paused')
	}

	pauseVideo() {
		l_pauseAllMedia(this)

		this.classList.remove('is-playing')
		this.classList.add('is-paused')
	}

	pauseOtherVideos() {
		l_pauseAllMedia(this.parentElement)

		this.parentElement.querySelectorAll('l-media-video').forEach((videoEl) => {
			const isLoaded = videoEl.classList.contains('is-loaded')

			if (isLoaded) {
				videoEl.classList.remove('is-playing')
				videoEl.classList.add('is-paused')
			}
		})
	}
}

customElements.define('l-media-video', LTAMediaVideo)

/*****************************************
 * [COMPONENT]: DRAWER TRIGGER
 ******************************************/

class LTAProductModalExternalMedia extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.templateEl = this.querySelector('template')
	}

	playVideo() {
		if (this.templateEl) {
			this.templateContentEl = this.templateEl.content.cloneNode(true)
		}

		this.appendChild(this.templateContentEl)
	}

	pauseVideo() {
		this.innerHTML = '' // Clear the container to stop the video
	}
}

customElements.define('l-external-media', LTAProductModalExternalMedia)

/*****************************************
 * [COMPONENT]:-DRAWER
 *****************************************/

class LTADrawer extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.setupDrawer()
	}

	setupDrawer() {
		// Elements
		this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
		this.closeBtnEls = this.querySelectorAll('[data-close]')
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
		this.triggerEl = null

		if (this.dataset.closeSelector) {
			const externalCloseBtnEls = document.querySelectorAll(this.dataset.closeSelector)
			this.closeBtnEls = Array.from(this.closeBtnEls).concat(Array.from(externalCloseBtnEls))
		}

		// Data
		this.onlyOneDrawerOpen = this.dataset.onlyOneDrawerOpen === 'true'
		this.drawerName = this.dataset.drawerName || 'drawer'

		// Setup events

		// Close this drawer if another drawer is open
		if (this.onlyOneDrawerOpen) {
			document.addEventListener('l:drawer:open', (event) => {
				if (event.detail.drawerName !== this.drawerName) {
					this.drawer_close()
				}
			})
		}

		// Open event
		this.triggerEls.forEach((triggerEl) => {
			triggerEl.addEventListener('click', (event) => {
				event.preventDefault()

				this.drawer_open(triggerEl)
			})
		})

		// Close event
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				this.drawer_close()
			})
		})
	}

	drawer_open(triggerEl) {
		l_setupVariablesBeforeOpenModal()
		l_lockBodyScroll(this.drawerName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
			l_trapFocus(this.drawerInnerEl, this.defaultElementToFocus)
		}

		// Set up accessibility
		this.setAttribute('aria-expanded', 'true')
		this.removeAttribute('tabindex')

		// Set global events
		window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))

		// Dispatch event to notify that the drawer is open
		document.dispatchEvent(
			new CustomEvent('l:drawer:open', {
				detail: { drawerName: this.drawerName },
			})
		)
	}

	drawer_close() {
		l_unLockBodyScroll(this.drawerName)

		// Close drawer
		this.classList.remove('is-active')

		window.l_debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			l_removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove accessibility
		this.setAttribute('aria-expanded', 'false')
		this.setAttribute('tabindex', '-1')

		// Remove global events
		window.removeEventListener('click', this.drawer_closeOutsideOnClick)
		window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		if (!this.classList.contains('is-active')) return

		const target = event.target
		const triggerSelector = this.dataset.drawerTriggerSelector || 'l-drawer-trigger'
		const isDrawerTrigger = event.target.closest(triggerSelector) || event.target.tagName == triggerSelector

		const isOutsideClick =
			!this.drawerInnerEl.contains(target) &&
			!Array.from(this.triggerEls).some((el) => el.contains(target)) &&
			!isDrawerTrigger

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}
}

customElements.define('l-drawer', LTADrawer)

/*****************************************
 * [COMPONENT]: DRAWER TRIGGER
 ******************************************/

class LTADrawerTrigger extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.drawerEl = document.querySelector(this.dataset.selector)

		// Events
		if (this.drawerEl) {
			this.addEventListener('click', () => {
				this.drawerEl?.drawer_open?.(this)
			})
		}
	}
}

customElements.define('l-drawer-trigger', LTADrawerTrigger)

/*****************************************
 * [COMPONENT]: PRODUCT CARD
 ******************************************/
class LTAProductCard extends HTMLElement {
	constructor() {
		super()

		this.handleSwatchesClick = this.handleSwatchesClick.bind(this)
	}

	connectedCallback() {
		this.cacheElements()
		this.bindEvents()
	}

	cacheElements() {
		this.swatchesEl = this.querySelector('[data-swatches]')
		this.swatchesIconEls = this.querySelectorAll('[data-swatch]')
		this.variantImageEls = this.querySelectorAll('[data-variant-media]')
		this.atcButtonEl = this.querySelector('l-atc-button')
	}

	bindEvents() {
		if (this.swatchesEl) {
			l_addEventDelegate({
				context: this.swatchesEl,
				selector: '[data-swatch]',
				handler: this.handleSwatchesClick,
			})
		}
	}

	handleSwatchesClick(event) {
		event.preventDefault()
		const target = event.target.closest('[data-swatch]')
		if (!target) return

		const selectedVariantId = target.dataset.swatch
		if (this.atcButtonEl) {
			this.atcButtonEl.dataset.variantId = selectedVariantId
		}

		this.toggleVariantImages(selectedVariantId)
		this.toggleSwatchIcons(selectedVariantId)
	}

	toggleVariantImages(selectedVariantId) {
		this.variantImageEls.forEach((variantImageEl) => {
			const currentVariantId = variantImageEl.dataset.variantMedia
			variantImageEl.classList.toggle('l-hidden', currentVariantId != selectedVariantId)
		})
	}

	toggleSwatchIcons(selectedVariantId) {
		this.swatchesIconEls.forEach((swatchesIconEl) => {
			const currentVariantId = swatchesIconEl.dataset.swatch
			swatchesIconEl.classList.toggle('is-checked', currentVariantId == selectedVariantId)
		})
	}
}

if (!customElements.get('l-product-card')) {
	customElements.define('l-product-card', LTAProductCard)
}

/*****************************************
 * [COMPONENT]: RECENT VIEWS
 ******************************************/

if (!customElements.get('l-recent-view-products')) {
	customElements.define(
		'l-recent-view-products',
		class LTARecentViewsProducts extends HTMLElement {
			constructor() {
				super()

				this.fetchProductLocalStore = 'l-recent-view-products'
			}

			connectedCallback() {
				this.productsEl = this.querySelector('[data-products]')
				this.carouselEl = this.querySelector('l-swiper-carousel')

				this.fetchAndRenderProducts()
			}

			async fetchAndRenderProducts() {
				const handlesRaw = localStorage.getItem(this.fetchProductLocalStore) || ''
				const recentHandles = handlesRaw.split('##').filter(Boolean)

				console.log(recentHandles)

				if (!recentHandles.length) return

				const customClasses = this.carouselEl ? 'l-column swiper-slide' : 'l-column'
				let productsHTML = ''

				const fetchPromises = recentHandles.map((handle) => {
					const url = `${window.LTATheme.routes.root}products/${handle}?section_id=a-ajax-product-card`
					console.log(url)
					return fetch(url)
						.then((res) => res.text())
						.then((html) => {
							if (html.includes('#custom_classes')) {
								const htmlWithClasses = html.replace('#custom_classes', customClasses)
								const productCardEl = l_getSectionDOM(htmlWithClasses)
								if (productCardEl && productCardEl.innerHTML) {
									productsHTML += productCardEl.innerHTML
								}
							}
						})
				})

				await Promise.all(fetchPromises)

				if (productsHTML && this.productsEl) {
					this.productsEl.innerHTML = productsHTML
					if (this.parentElement) this.parentElement.classList.remove('l-hidden')
					if (this.carouselEl && this.carouselEl.init) this.carouselEl.init()
				}
			}
		}
	)
}

if (!customElements.get('l-recent-view-watch')) {
	customElements.define(
		'l-recent-view-watch',
		class LTARecentViewsWatch extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				const maxRecentViewProductHandles = 8
				const fetchProductLocalStore = 'l-recent-view-products'
				const productHandle = this.dataset.productHandle
				const currentRecentViewProductHandles = `${localStorage.getItem(fetchProductLocalStore)}`
					.split('##')
					.filter((item) => item && item !== 'null' && item !== 'undefined')

				let newRecentViewProductHandles = [...currentRecentViewProductHandles, productHandle]

				// Remove duplicates and keep only the most recent (preserve order, last seen at end)
				newRecentViewProductHandles = newRecentViewProductHandles.filter(
					(item, index, arr) => arr.indexOf(item) === index
				)

				// If more than 8, remove oldest (front)
				if (newRecentViewProductHandles.length > maxRecentViewProductHandles) {
					newRecentViewProductHandles = newRecentViewProductHandles.slice(-maxRecentViewProductHandles)
				}

				localStorage.setItem(fetchProductLocalStore, newRecentViewProductHandles.join('##'))
			}
		}
	)
}

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

if (!customElements.get('l-atc-button')) {
	customElements.define(
		'l-atc-button',
		class LTACTAButton extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.buttonEl = this.querySelector('button')

				// Events
				this.buttonEl.addEventListener('click', this.handleAtcBtnClick.bind(this))
			}

			handleAtcBtnClick() {
				this.buttonEl.classList.add('is-loading', 'l-button-loading')

				const formData = ltaCartService.createConfigFromItems([
					{
						id: this.dataset.variantId,
						quantity: parseInt(this.dataset.quantity),
						...(this.dataset.sellingPlanId ? { selling_plan: this.dataset.sellingPlanId } : {}),
					},
				])

				ltaCartService
					.addToCart(formData, 'l-atc-button')
					.then((res) => {
						console.log(res)
					})
					.catch((err) => {
						console.error(err)
					})
					.finally(() => {
						this.buttonEl.classList.remove('is-loading', 'l-button-loading')
					})
			}
		}
	)
}

/*****************************************
 * [COMPONENT]: QUANTITY INPUT
 ******************************************/
if (!customElements.get('l-qty-input')) {
	class LTAQtyInput extends HTMLElement {
		constructor() {
			super()

			this._boundOnInputBeforeChange = this.onInputBeforeChange.bind(this)
			this._boundOnInputChange = this.onInputChange.bind(this)
			this._boundOnButtonClick = this.onButtonClick.bind(this)
		}

		connectedCallback() {
			this.input = this.querySelector('input')
			this.buttonMinus = this.querySelector("button[name='minus']")
			this.buttonPlus = this.querySelector("button[name='plus']")

			if (!this.input) return

			this.changeEvent = new Event('change', { bubbles: true })

			this.setupEventListeners()
			this.validateQtyRules()
		}

		setupEventListeners() {
			this.input.addEventListener('input', this._boundOnInputBeforeChange)
			this.input.addEventListener('change', this._boundOnInputChange)
			this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this._boundOnButtonClick))
		}

		onInputBeforeChange(e) {
			// Use local variable to prevent reference confusion if called as unbound event listener
			const input = this.input
			const value = parseInt(input.value, 10)

			if (isNaN(value)) return

			const min = input.min !== undefined ? parseInt(input.min, 10) : NaN
			const max = input.max !== undefined ? parseInt(input.max, 10) : NaN
			let newValue = value

			if (!isNaN(min) && value < min) {
				newValue = min
			}
			if (!isNaN(max) && value > max) {
				newValue = max
			}
			if (newValue !== value) {
				input.value = newValue
			}
		}

		onInputChange(e) {
			this.validateQtyRules()
		}

		onButtonClick(event) {
			event.preventDefault()

			const previousValue = this.input.value
			const buttonName = event.currentTarget.name

			if (buttonName === 'plus') {
				this.incrementQuantity()
			} else if (buttonName === 'minus') {
				this.decrementQuantity()
			}

			if (previousValue !== this.input.value) {
				this.input.dispatchEvent(this.changeEvent)
				this.onQuantityChanged(previousValue, parseInt(this.input.value, 10))
			}
		}

		onQuantityChanged(oldValue, newValue) {
			// Override in child classes if needed
		}

		incrementQuantity() {
			const input = this.input
			const min = input.hasAttribute('data-min')
				? parseInt(input.dataset.min, 10)
				: input.min
					? parseInt(input.min, 10)
					: 1
			const step = input.step ? parseInt(input.step, 10) : 1
			const value = parseInt(input.value, 10)

			if (!isNaN(min) && !isNaN(step) && value === 0 && min > step) {
				input.value = min
			} else {
				try {
					input.stepUp()
				} catch {
					input.value = (!isNaN(value) ? value : 0) + step
				}
			}
		}

		decrementQuantity() {
			const input = this.input
			const min = input.hasAttribute('data-min')
				? parseInt(input.dataset.min, 10)
				: input.min
					? parseInt(input.min, 10)
					: 1

			try {
				input.stepDown()
			} catch {
				const value = parseInt(input.value, 10)
				const step = input.step ? parseInt(input.step, 10) : 1
				input.value = (!isNaN(value) ? value : 0) - step
			}

			// Clamp to min if needed
			if (!isNaN(min) && parseInt(input.value, 10) < min) {
				input.value = min
			}
		}

		validateQtyRules() {
			const input = this.input
			const value = parseInt(input.value, 10)

			if (this.buttonMinus) {
				const min = input.min ? parseInt(input.min, 10) : 1
				this.buttonMinus.toggleAttribute('disabled', !input.value || value <= min)
			}

			if (this.buttonPlus) {
				const max = input.max ? parseInt(input.max, 10) : NaN
				this.buttonPlus.toggleAttribute('disabled', input.value && !isNaN(max) && value >= max)
			}
		}

		disconnectedCallback() {
			if (this.input) {
				this.input.removeEventListener('input', this._boundOnInputBeforeChange)
				this.input.removeEventListener('change', this._boundOnInputChange)
			}
			this.querySelectorAll('button').forEach((button) => button.removeEventListener('click', this._boundOnButtonClick))
		}
	}

	customElements.define('l-qty-input', LTAQtyInput)
}

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

if (!customElements.get('l-added-to-cart-modal')) {
	class LTAAddedToCartModal extends customElements.get('l-drawer') {
		constructor() {
			super()
			this._onProductAdded = this._onProductAdded.bind(this)
		}

		connectedCallback() {
			if (super.connectedCallback) super.connectedCallback()

			this.productsEl = this.querySelector('[data-products]')
			this.footerEl = this.querySelector('[data-footer]')
			document.addEventListener('l:product:added', this._onProductAdded)
		}

		disconnectedCallback() {
			document.removeEventListener('l:product:added', this._onProductAdded)
			if (super.disconnectedCallback) super.disconnectedCallback()
		}

		async _onProductAdded(event) {
			const addedLineItem = event.detail?.response?.items?.[0] || event.detail?.response
			const key = addedLineItem?.key

			if (!addedLineItem?.url) return

			try {
				const res = await fetch(addedLineItem.url)
				const html = await res.text()
				const addedToCartModal = l_getSectionDOM(html, '#added-to-cart-modal')

				if (!addedToCartModal) return

				const productsHTML = addedToCartModal.querySelector('[data-products]')
				const footerHTML = addedToCartModal.querySelector('[data-footer]')

				if (this.productsEl && productsHTML) {
					this.productsEl.innerHTML = productsHTML.innerHTML
				}

				if (this.footerEl && footerHTML) {
					this.footerEl.innerHTML = footerHTML.innerHTML
				}

				this.querySelectorAll('[data-line-item]').forEach((lineItemEl) => {
					lineItemEl.classList.toggle('l-hidden', lineItemEl.dataset.lineItem !== key)
				})
			} finally {
				this.drawer_open && this.drawer_open()
			}
		}
	}

	customElements.define('l-added-to-cart-modal', LTAAddedToCartModal)
}

/************************************************************************************

* RUN GLOBAL FUNCTIONALITIES / RUN BASE FUNCTIONALITIES

*************************************************************************************/

window.addEventListener('DOMContentLoaded', () => {
	// Initialize
	l_setupGlobalVariables()
	l_addClassesToRteTables()
	l_setupVariablesBeforeOpenModal()
	l_smoothScrollOnAnchorTags()
})

window.addEventListener(
	'scroll',
	l_debounce(() => {
		// Update variables
		l_setupVariablesBeforeOpenModal()
	}, 20)
)

window.addEventListener(
	'resize',
	l_debounce(() => {
		// Update variables
		l_setupVariablesBeforeOpenModal()
	}, 20)
)

if (window.visualViewport) {
	window.visualViewport.addEventListener(
		'resize',
		l_debounce(() => {
			l_setupVariablesBeforeOpenModal()
		}, 30)
	)
}

/**********************************
 * HANDLE UPDATE CART COUNT
 **********************************/
function fetchAndUpdateCartCount(params) {
	fetch(`${window.LTATheme.routes.cart_url}.js`)
		.then((res) => res.json())
		.then((cartData) => {
			const cartCountEls = document.querySelectorAll('[data-cart-count]')
			cartCountEls.forEach((cartCountEl) => {
				cartCountEl.innerHTML = cartData.item_count
			})
		})
}

document.addEventListener('l:product:added', (event) => {
	fetchAndUpdateCartCount()
})
document.addEventListener('l:cart:error', (event) => {
	fetchAndUpdateCartCount()
})
document.addEventListener('l:cart:updated', (event) => {
	const cartData = event.detail.cart

	const cartCountEls = document.querySelectorAll('[data-cart-count]')
	cartCountEls.forEach((cartCountEl) => {
		cartCountEl.innerHTML = cartData.item_count
	})
})

